import { useLocation } from "react-router-dom";
import Link from "@/components/LocalizedLink";
import { ArrowRight, ArrowUp, ExternalLink } from "lucide-react";
import {
  CONTACT_EMAIL,
  LINKEDIN_URL,
  serviceCatalogue,
  getCtaLabel,
  localize,
  siteContent,
} from "@/content/site";
import { useTranslation } from "@/i18n/useTranslation";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import VertLogo from "@/components/VertLogo";
import { stripLocalePrefix } from "@/i18n/localePath";

type FooterLink = {
  href: string;
  label: string;
  external?: boolean;
};

const isActivePath = (pathname: string, href: string) => (
  href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`)
);

const Footer = () => {
  const { t, locale } = useTranslation();
  const location = useLocation();
  const activePathname = stripLocalePrefix(location.pathname);
  const isFr = locale === "fr";
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.verificationReview, locale, "shortLabel");
  const positioning = localize(siteContent.brand.shortPositioning, locale);
  const serviceLinks: FooterLink[] = serviceCatalogue.map((service) => ({
    href: service.href,
    label: localize(service.name, locale),
  }));
  const industryLinks: FooterLink[] = siteContent.industries.map((industry) => ({
    href: industry.href,
    label: localize(industry.label, locale),
  }));
  const companyLinks: FooterLink[] = [
    { href: "/work", label: t("nav.work") },
    { href: "/about", label: t("nav.about") },
    { href: "/solutions", label: isFr ? "Catalogue de solutions" : "Solutions catalogue" },
    { href: "/contact", label: isFr ? "Discuter d’un processus coûteux" : "Discuss a costly workflow" },
    { href: "/privacy", label: isFr ? "Confidentialité" : "Privacy" },
  ];
  const insightLinks: FooterLink[] = [
    { href: "/insights", label: t("nav.insights") },
    { href: "/insights/vet-ai-vendor", label: isFr ? "Évaluer un fournisseur d’IA" : "How to vet an AI vendor" },
    { href: "/insights/production-grade-ai", label: isFr ? "IA en production" : "Production-grade AI" },
    { href: "/insights/workflows-deserve-ai", label: isFr ? "Quels processus méritent l’IA" : "Which workflows deserve AI" },
  ];

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  };

  const renderLinks = (links: FooterLink[]) => (
    <ul className="space-y-2.5 text-[12.5px] leading-snug text-[hsl(var(--sd-fg)/0.62)] sm:space-y-3.5">
      {links.map((link) => (
        <li key={`${link.href}-${link.label}`}>
          {link.external ? (
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="focus-ring inline-flex min-h-9 items-center gap-1.5 py-1 transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] sm:min-h-0 sm:py-0"
            >
              {link.label}
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          ) : (
            <Link
              to={link.href}
              aria-current={isActivePath(activePathname, link.href) ? "page" : undefined}
              className="focus-ring inline-flex min-h-9 items-center py-1 transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] aria-[current=page]:text-[hsl(var(--sd-fg))] sm:min-h-0 sm:py-0"
            >
              {link.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <footer className="section-dark grain relative" aria-labelledby="site-footer-heading">
      <div className="shimmer-line" />

      <div className="container-wide relative z-10 pb-12 pt-20 md:pb-14 md:pt-28">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-[1.05fr_2fr] lg:gap-20">
          <div className="max-w-md">
            <Link to="/" className="focus-ring group mb-6 inline-flex items-center" aria-label={isFr ? "Accueil de Vert AI" : "Vert AI home"}>
              <VertLogo className="h-5 w-auto text-[hsl(var(--sd-fg))] transition-opacity duration-300 group-hover:opacity-60" />
              <span className="sr-only">Vert AI</span>
            </Link>
            <h2 id="site-footer-heading" className="sr-only">
              {isFr ? "Navigation du pied de page de Vert AI" : "Vert AI footer navigation"}
            </h2>
            <p className="max-w-[28rem] text-[14px] leading-[1.8] text-[hsl(var(--sd-fg)/0.58)]">
              {positioning}
            </p>
            <PrimaryCtaLink
              cta={siteContent.ctas.verificationReview}
              labelVariant="shortLabel"
              dataEventLocation="footer"
              dataEventSurface="site_footer"
              className="focus-ring mt-8 inline-flex items-center gap-2 border border-[hsl(var(--sd-fg)/0.22)] px-5 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-[hsl(var(--sd-fg))] transition-all duration-500 hover:border-primary/55 hover:text-primary"
            >
              {primaryCtaLabel}
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </PrimaryCtaLink>
            <div className="mt-9 space-y-2 text-[12px] leading-[1.7] text-[hsl(var(--sd-fg)/0.62)]">
              <Link
                to="/contact"
                aria-current={isActivePath(activePathname, "/contact") ? "page" : undefined}
                className="focus-ring flex min-h-9 items-center transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] aria-[current=page]:text-[hsl(var(--sd-fg))] sm:min-h-0"
              >
                {isFr ? "Contacter Vert AI" : "Contact Vert AI"}
              </Link>
              <a href={`mailto:${CONTACT_EMAIL}`} className="focus-ring flex min-h-9 items-center break-all transition-colors duration-400 hover:text-[hsl(var(--sd-fg)/0.92)] sm:min-h-0">
                {CONTACT_EMAIL}
              </a>
              <p>{t("contact.location")}</p>
            </div>
          </div>

          <nav
            className="grid grid-cols-1 gap-10 sm:grid-cols-2 xl:grid-cols-4"
            aria-label={isFr ? "Liens du pied de page" : "Footer links"}
          >
            <div>
              <h3 className="mb-6 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.6)]">
                {t("footer.services")}
              </h3>
              {renderLinks(serviceLinks)}
            </div>

            <div>
              <h3 className="mb-6 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.6)]">
                {t("footer.industries")}
              </h3>
              {renderLinks(industryLinks)}
            </div>

            <div>
              <h3 className="mb-6 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.6)]">
                {t("footer.company")}
              </h3>
              {renderLinks([
                ...companyLinks,
                ...(LINKEDIN_URL ? [{ href: LINKEDIN_URL, label: "LinkedIn", external: true }] : []),
              ])}
            </div>

            <div>
              <h3 className="mb-6 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--sd-fg)/0.6)]">
                {t("footer.insights")}
              </h3>
              {renderLinks(insightLinks)}
            </div>
          </nav>
        </div>
      </div>

      <div className="container-wide relative z-10 pb-8">
        <div className="mb-7 h-px bg-[hsl(var(--sd-fg)/0.04)]" />
        <div className="flex flex-col items-start justify-between gap-5 text-[11px] text-[hsl(var(--sd-fg)/0.62)] sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} {t("footer.copyright")}</p>
          <div className="flex items-center gap-6">
            <p className="font-mono text-[10px] tracking-[0.12em]">{t("footer.bottomTagline")}</p>
            <button
              onClick={scrollToTop}
              className="focus-ring flex h-11 w-11 items-center justify-center rounded-sm border border-[hsl(var(--sd-fg)/0.24)] text-[hsl(var(--sd-fg)/0.75)] transition-all duration-500 hover:border-primary/45 hover:text-[hsl(var(--sd-fg))] hover:shadow-[0_0_12px_hsl(var(--primary)/0.08)]"
              aria-label={isFr ? "Revenir en haut de la page" : "Back to top"}
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
